import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * A square media gallery that wraps around on swipe.
 *
 * Horizon's own <slideshow-component> cannot do this: its `infinite` attribute
 * only reaches select(), so arrows and dots wrap while a swipe still hits the
 * end of the track (assets/slideshow.js:231, :431). Wrapping a *swipe* needs
 * clones, which is why this exists.
 *
 * Layout is CSS scroll-snap; this class only adds the clones, the wrap-around,
 * the dot state and the video pause control.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scroll container holding the slides.
 * @property {HTMLElement[]} [slides] - The authored slides, clones excluded.
 * @property {HTMLButtonElement[]} [dots] - One button per authored slide.
 *
 * @extends {Component<Refs>}
 */
class LiftGallery extends Component {
  requiredRefs = ['track'];

  /** Scroll events are ignored until the first slide has been positioned. */
  #ready = false;

  /** Index into the authored slides, clones excluded. */
  #index = 0;

  #wired = false;
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  /**
   * The theme editor re-renders a section through the Section Rendering API.
   * The server HTML carries no clones, so they have to be rebuilt — and #setup
   * is written to be safe to run again.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  // -- slides ---------------------------------------------------------------

  /** Authored slides only. refs.slides is kept clone-free by #clone(). */
  get #slides() {
    return this.refs.slides ?? [];
  }

  /** Every slide in the track, clones included, in DOM order. */
  get #allSlides() {
    return [...this.refs.track.querySelectorAll('[data-lift-slide]')];
  }

  #setup() {
    const slides = this.#slides;

    // Video first: a gallery of one slide still needs its pause control, and
    // WCAG 2.2 SC 2.2.2 does not care how many slides there are.
    this.#wireVideos();
    if (prefersReducedMotion()) {
      for (const video of this.querySelectorAll('video')) video.pause();
    }
    this.#syncVideoButtons();

    this.classList.toggle('lift-gallery--single', slides.length < 2);
    if (slides.length < 2) return;

    this.#buildClones();
    this.#wire();
    this.#goTo(this.#index, { instant: true });
    this.#ready = true;
    this.#syncDots();
  }

  /**
   * Last slide cloned to the front, first cloned to the back, so a swipe past
   * either end lands on a copy of the slide it should wrap to. #onScroll then
   * teleports to the real one once the scroll has settled.
   */
  #buildClones() {
    for (const old of this.refs.track.querySelectorAll('[data-lift-clone]')) old.remove();

    const slides = this.#slides;
    const first = slides[0];
    const last = slides[slides.length - 1];
    if (!first || !last) return;

    first.before(this.#clone(last));
    last.after(this.#clone(first));
  }

  /**
   * Component keeps refs in sync through a MutationObserver on childList, so a
   * clone that kept its ref attributes would end up inside refs.slides and
   * corrupt every index. Strip them. `inert` keeps the copy out of the
   * accessibility tree and the tab order, so duplicate buttons and duplicate
   * alt text never reach a screen reader.
   *
   * @param {HTMLElement} slide
   */
  #clone(slide) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));

    clone.removeAttribute('ref');
    for (const node of clone.querySelectorAll('[ref]')) node.removeAttribute('ref');
    for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');

    clone.setAttribute('data-lift-clone', '');
    clone.setAttribute('inert', '');
    clone.setAttribute('aria-hidden', 'true');

    return clone;
  }

  // -- geometry -------------------------------------------------------------

  /**
   * Scroll offset that centres a slide, clamped to the track. Centring is what
   * the mobile peek needs; on desktop a slide fills the track, so the same
   * formula simply aligns it.
   *
   * @param {HTMLElement} slide
   */
  #offsetOf(slide) {
    const track = this.refs.track;
    const raw = slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;

    return Math.max(0, Math.min(track.scrollWidth - track.clientWidth, raw));
  }

  /** The slide currently closest to the scroll position, clones included. */
  #nearestSlide() {
    const track = this.refs.track;
    const slides = this.#allSlides;
    const centre = track.scrollLeft + track.clientWidth / 2;

    let best = slides[0];
    let bestGap = Infinity;

    for (const slide of slides) {
      const gap = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - centre);
      if (gap < bestGap) {
        bestGap = gap;
        best = slide;
      }
    }

    return best;
  }

  /**
   * @param {number} index - Index into the authored slides. Wraps.
   * @param {{instant?: boolean}} [options]
   */
  #goTo(index, options = {}) {
    const slides = this.#slides;
    if (!slides.length) return;

    const wrapped = ((index % slides.length) + slides.length) % slides.length;
    const slide = slides[wrapped];
    if (!slide) return;

    this.#index = wrapped;
    this.refs.track.scrollTo({
      left: this.#offsetOf(slide),
      behavior: options.instant || prefersReducedMotion() ? 'instant' : 'smooth',
    });
    this.#syncDots();
  }

  // -- wiring ---------------------------------------------------------------

  /** Idempotent: a re-render replaces the videos, and listeners go with them. */
  #wireVideos() {
    for (const video of this.querySelectorAll('video')) {
      video.removeEventListener('play', this.#syncVideoButtons);
      video.removeEventListener('pause', this.#syncVideoButtons);
      video.addEventListener('play', this.#syncVideoButtons);
      video.addEventListener('pause', this.#syncVideoButtons);
    }
  }

  #wire() {
    if (this.#wired) return;
    this.#wired = true;

    this.refs.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.refs.track.addEventListener('keydown', this.#onKeydown);

    // Slides are sized in percentages, so every width change moves the
    // scroll offsets: window resize, a late image, a web font swapping in.
    // Re-anchoring on resize replaces the prototype's three setTimeout calls.
    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(this.refs.track);
  }

  #onScroll = debounce(() => {
    if (!this.#ready) return;

    const slide = this.#nearestSlide();
    if (!slide) return;

    if (slide.hasAttribute('data-lift-clone')) {
      // Swiped onto a copy: jump to the original without animating, so the
      // track appears to have wrapped.
      const slides = this.#slides;
      const isHead = slide === this.refs.track.firstElementChild;

      this.#goTo(isHead ? slides.length - 1 : 0, { instant: true });
      return;
    }

    const index = this.#slides.indexOf(slide);
    if (index >= 0 && index !== this.#index) {
      this.#index = index;
      this.#syncDots();
    }
  }, 90);

  #onResize = debounce(() => {
    if (!this.#ready) return;
    this.#goTo(this.#index, { instant: true });
  }, 120);

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  };

  #syncDots() {
    const dots = this.refs.dots ?? [];

    dots.forEach((dot, index) => {
      const current = index === this.#index;

      // aria-current, not aria-selected: aria-selected is only valid on
      // option/tab/row/gridcell, and a plain button is none of those.
      if (current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  // -- public, called from on:click in the markup ----------------------------

  next() {
    this.#goTo(this.#index + 1);
  }

  previous() {
    this.#goTo(this.#index - 1);
  }

  /** @param {number|string} index */
  select(index) {
    this.#goTo(Number(index));
  }

  /**
   * One control per slide that holds a video, toggling that slide's video and
   * the copy of it inside the clone.
   *
   * Horizon's <deferred-media> cannot cover this case: its play/pause hint is
   * gated on [data-media-loaded] (assets/media.js:127), which an inline
   * autoplaying video never gets, so the control stays invisible.
   *
   * @param {Event} event
   */
  toggleVideo(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[data-lift-for]');
    const id = /** @type {HTMLElement | null} */ (button)?.dataset.liftFor;
    if (id == null) return;

    const videos = this.#videosFor(id);
    // Mirror the test in #syncVideoButtons, so the button always does what its
    // label says. `some(paused)` would be wrong: a slide and its clone can
    // disagree — the clone's autoplay can be refused while the original plays —
    // and then a press meant to pause would start everything instead.
    const playing = videos.some((video) => !video.paused);

    for (const video of videos) {
      if (playing) video.pause();
      else video.play().catch(() => {});
    }
    // The buttons are not updated here on purpose — the play/pause events do
    // it, so the control also tells the truth when autoplay was refused or the
    // video ends on its own.
  }

  /** @param {string} id */
  #videosFor(id) {
    return /** @type {HTMLVideoElement[]} */ ([
      ...this.querySelectorAll(`[data-lift-slide="${CSS.escape(id)}"] video`),
    ]);
  }

  /**
   * Reads the state off the videos rather than tracking it, so the button can
   * never drift from what is actually on screen.
   */
  #syncVideoButtons = () => {
    for (const button of this.querySelectorAll('[data-lift-for]')) {
      const id = /** @type {HTMLElement} */ (button).dataset.liftFor;
      if (id == null) continue;

      const videos = this.#videosFor(id);
      const paused = videos.length > 0 && videos.every((video) => video.paused);

      button.setAttribute('aria-pressed', String(paused));

      const label = button.querySelector('[data-lift-label]');
      const text = /** @type {HTMLElement} */ (button).dataset[
        paused ? 'liftLabelPlay' : 'liftLabelPause'
      ];
      if (label && text) label.textContent = text;
    }
  };
}

if (!customElements.get('lift-gallery-component')) {
  customElements.define('lift-gallery-component', LiftGallery);
}
